// src/forms/data_hub_cors_user_data.js
// Extracted from data_hub.js — handles CORS iframe user data response merging.

 import { white_list_outgoing_person_fields, listArrayKeys } from '../helpers/static_vars';
import { validate_and_clean_handlebars_from_user_data } from '../helpers/handlebars_validation';
import { re_prefill_all_forms } from './shared_form_ux_functions';
import { updateActblueConfigWithDatahub } from '../helpers/actblue_integration';
import { merge_user_data_browser } from '../helpers/user_data_smart_merge';
import { write_organization_jwt_cache_entry } from '../helpers/organization_jwt_local_storage_cache';

/**
 * After merging CORS payload into client state, align subscription list fields when the
 * server payload only included one of the paired arrays (JWT / user_jwt omit empty side).
 *
 * @param {Object} merged_user_data
 * @param {Object} cors_payload_cleaned
 * @returns {Object}
 */
function apply_cors_subscription_list_consistency_after_merge(merged_user_data, cors_payload_cleaned) {
	const cors_subscription_arrays = {};
	listArrayKeys.forEach((key) => {
		if (Array.isArray(cors_payload_cleaned[key])) {
			cors_subscription_arrays[key] = cors_payload_cleaned[key];
		}
	});
	if (Object.keys(cors_subscription_arrays).length === 0) {
		return merged_user_data;
	}
	const out = { ...merged_user_data };
	if (
		cors_subscription_arrays.unsubscribedMessageTypeIds &&
		!cors_subscription_arrays.subscribedMessageTypeIds
	) {
		out.subscribedMessageTypeIds = [];
	}
	if (
		cors_subscription_arrays.subscribedMessageTypeIds &&
		!cors_subscription_arrays.unsubscribedMessageTypeIds
	) {
		out.unsubscribedMessageTypeIds = [];
	}
	return out;
}

/**
 * Processes CORS iframe user data response.
 * Whitelist-filters, validates handlebars, smart-merges with merge_user_data_browser
 * (incoming CORS wins on conflict), updates user_data, optional JWT cache write,
 * re-prefills forms, and dispatches the data-hub-new-user-data event.
 *
 * @param {Object} data_hub_instance - The data_hub class instance.
 * @param {Object} payload - Whitelisted user data from the CORS iframe.
 */
export async function got_cors_user_data_cache(data_hub_instance, payload) {
	if (!payload) return;

	if (typeof payload !== 'object') {
		return;
	}

	Object.keys(payload).forEach((key) => {
		if (!white_list_outgoing_person_fields.includes(key)) {
			delete payload[key];
		}
	});

	let newMergedUserData;
	try {
		const cleaned_payload = validate_and_clean_handlebars_from_user_data(
			payload,
			white_list_outgoing_person_fields,
			data_hub_instance.error_function
		);

		const base_for_merge = {
			...(window?.data_hub_user_data || {}),
			...(data_hub_instance.user_data || {}),
		};

		let merged = await merge_user_data_browser(base_for_merge, cleaned_payload || {});
		merged = apply_cors_subscription_list_consistency_after_merge(merged, cleaned_payload || {});

		newMergedUserData = merged;
		data_hub_instance.user_data = newMergedUserData;
		window.data_hub_user_data = newMergedUserData;

		const jwt_after_message = data_hub_instance.jwt_cookie;
		if (typeof jwt_after_message === 'string' && jwt_after_message.length > 0) {
			write_organization_jwt_cache_entry(data_hub_instance, jwt_after_message);
		}
	} catch (error) {
		data_hub_instance.error_function?.(`Error validating CORS payload for handlebars: ${error}`, null);
		const fallback_base = {
			...(window?.data_hub_user_data || {}),
			...(data_hub_instance.user_data || {}),
		};
		newMergedUserData = await merge_user_data_browser(fallback_base, payload);
		newMergedUserData = apply_cors_subscription_list_consistency_after_merge(newMergedUserData, payload);
		data_hub_instance.user_data = newMergedUserData;
		window.data_hub_user_data = newMergedUserData;
		const jwt_after_message = data_hub_instance.jwt_cookie;
		if (typeof jwt_after_message === 'string' && jwt_after_message.length > 0) {
			write_organization_jwt_cache_entry(data_hub_instance, jwt_after_message);
		}
	}

	if (typeof data_hub_instance.resolve_user_data_loaded === 'function') {
		data_hub_instance.resolve_user_data_loaded();
	}

	document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
		detail: {
			user_data: data_hub_instance.user_data || {},
			prefill_input_selector: data_hub_instance.prefill_input_selector || null,
			data_hub_instance: data_hub_instance,
			target: data_hub_instance.target || document,
			source: 'cors_iframe'
		},
		bubbles: true,
		composed: true
	}));

	re_prefill_all_forms(newMergedUserData, data_hub_instance.prefill_input_selector, data_hub_instance);

	updateActblueConfigWithDatahub(data_hub_instance);
}
